import { useCallback, useEffect, useMemo, useState } from 'react'
import { FilterBar } from '../components/common/FilterBar'
import { CarFront, ShieldAlert, AlertCircle, Wrench, User, UserCog, CalendarClock, MapPin } from 'lucide-react'
import { useGetOffRoadVehicles } from '../hooks/backend/fleet'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { Switch } from '../lib/shadcn/switch'
import { Label } from '../lib/shadcn/label'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { OffRoadUpdateDialog, ReturnToRoadButton } from '../components/fleet/OffRoadUpdateDialog'
import { formatDate } from '../utils/fleet'
import { type OffRoadRow, offRoadReasonLabel, updateStateChip } from '../utils/servicing'

/**
 * Vehicles that are not operational, and when each one is next reported on.
 *
 * The ordering is the whole design. An overdue update, and a vehicle with no
 * update date at all, both rank above a long outage that somebody is actively
 * chasing. When this page was built the fleet had thirteen vehicles off the
 * road, 1,391 vehicle-days between them, not one of them carrying a next update
 * date, and five sitting behind an incident already marked resolved. Sorting by
 * days-down alone would have shown the same thirteen rows and told nobody which
 * ones had been abandoned.
 */
export default function OffRoad() {
  const { loading: accessLoading, canView, canEditFleet, caption } = useCapabilities()
  const [search, setSearch] = useState('')
  const [includeGone, setIncludeGone] = useState(false)

  const feed = useGetOffRoadVehicles()
  const trigger = feed.trigger

  const load = useCallback(() => {
    trigger({ scope: includeGone ? 'all' : 'in_fleet' }, { skipCache: true })
  }, [trigger, includeGone])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const allRows = (feed.data as OffRoadRow[] | undefined) ?? []

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return allRows
    return allRows.filter((r) =>
      [r.registration_number, r.owner_name, r.manager_name, r.reason, r.make, r.model]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term),
    )
  }, [allRows, search])

  const totals = useMemo(() => {
    const days = allRows.reduce((sum, r) => sum + (r.days_off_road ?? 0), 0)
    return {
      count: allRows.length,
      days,
      needsUpdate: allRows.filter(
        (r) => r.update_state === 'update_overdue' || r.update_state === 'no_update_date',
      ).length,
      limbo: allRows.filter((r) => r.incident_already_resolved).length,
      longest: allRows.reduce((m, r) => Math.max(m, r.days_off_road ?? 0), 0),
    }
  }, [allRows])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Off-road vehicles are available to admins only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Wrench className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Not operational</h1>
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {feed.loading ? '…' : `${rows.length} vehicle${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

        <div className="mt-3 flex flex-wrap gap-2">
          <Chip tone={totals.count > 0 ? 'red' : 'green'}>
            Off the road
            <span className="ml-1 font-semibold tabular-nums">{totals.count}</span>
          </Chip>
          <Chip tone="neutral">
            Vehicle-days lost
            <span className="ml-1 font-semibold tabular-nums">{totals.days.toLocaleString('en-ZA')}</span>
          </Chip>
          <Chip tone={totals.needsUpdate > 0 ? 'red' : 'green'}>
            Needs an update
            <span className="ml-1 font-semibold tabular-nums">{totals.needsUpdate}</span>
          </Chip>
          <Chip tone={totals.limbo > 0 ? 'amber' : 'green'}>
            Incident already resolved
            <span className="ml-1 font-semibold tabular-nums">{totals.limbo}</span>
          </Chip>
          <Chip tone="neutral">
            Longest
            <span className="ml-1 font-semibold tabular-nums">{totals.longest}d</span>
          </Chip>
        </div>
      </div>

      <FilterBar
        activeCount={(search.trim() ? 1 : 0) + (includeGone ? 1 : 0)}
        summary={[
          search.trim() ? `"${search.trim()}"` : null,
          includeGone ? 'including offboarded' : 'excluding offboarded',
        ]
          .filter(Boolean)
          .join(', ')}
        trailing={`${rows.length} vehicles`}
      >
      <div className="space-y-2 p-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by registration, owner, manager or reason"
        />
        <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
          <Switch id="include-gone" checked={includeGone} onCheckedChange={setIncludeGone} />
          <Label htmlFor="include-gone" className="cursor-pointer text-xs font-normal">
            Include offboarded and written off
          </Label>
        </div>
        {!includeGone && (
          <p className="text-xs text-muted-foreground">
            Offboarded and written-off vehicles are hidden by default. Fifty of the sixty-three
            vehicles flagged non-operational are in one of those two states and are not coming back,
            so showing them would bury the ones that are.
          </p>
        )}
      </div>
      </FilterBar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {feed.error ? (
          <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{feed.error}</span>
          </div>
        ) : feed.loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <CarFront className="h-6 w-6" />
            Every vehicle in the fleet is on the road.
          </div>
        ) : (
          <ul>
            {rows.map((r) => {
              const state = updateStateChip(r.update_state, r.update_days_overdue)
              const urgent = r.update_state === 'update_overdue' || r.update_state === 'no_update_date'
              return (
                <li key={r.off_road_id}>
                  <div
                    className={cn(
                      'flex flex-col gap-2 border-b border-border px-3 py-3 sm:flex-row sm:items-start',
                      urgent && 'bg-rose-50/50 dark:bg-rose-950/20',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-mono text-sm font-bold uppercase tracking-wide text-foreground">
                          {r.registration_number || '—'}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {[r.make, r.model].filter(Boolean).join(' ') || 'Unknown vehicle'}
                        </span>
                        <span className="text-xs font-semibold tabular-nums text-foreground">
                          {r.days_off_road} day{r.days_off_road === 1 ? '' : 's'} down
                        </span>
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        {r.owner_name && (
                          <span className="inline-flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {r.owner_name}
                            {r.owner_phone ? ` · ${r.owner_phone}` : ''}
                          </span>
                        )}
                        {r.manager_name && (
                          <span className="inline-flex items-center gap-1">
                            <UserCog className="h-3 w-3" />
                            {r.manager_name}
                          </span>
                        )}
                        {r.current_location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {r.current_location}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <CalendarClock className="h-3 w-3" />
                          Since {formatDate(r.started_at)}
                        </span>
                      </div>

                      {r.reason && (
                        <p className="mt-1.5 text-xs text-foreground">{r.reason}</p>
                      )}
                      {r.last_update_note && (
                        <p className="mt-1 text-[11px] italic text-muted-foreground">
                          {r.last_update_note}
                          {r.last_update_at ? ` (${formatDate(r.last_update_at)})` : ''}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:flex-col sm:items-end">
                      <Chip tone={state.tone}>{state.label}</Chip>
                      <Chip tone="neutral">{offRoadReasonLabel(r.reason_category)}</Chip>
                      {r.incident_already_resolved && (
                        <Chip tone="amber">Incident resolved, still down</Chip>
                      )}
                      {r.return_overdue && <Chip tone="red">Past expected return</Chip>}
                      {r.next_update_date && (
                        <span className="text-[11px] text-muted-foreground">
                          Next update {formatDate(r.next_update_date)}
                        </span>
                      )}
                      {canEditFleet && (
                        <div className="flex flex-wrap items-center gap-2 pt-1 sm:justify-end">
                          <OffRoadUpdateDialog row={r} onSaved={load} />
                          <ReturnToRoadButton row={r} onSaved={load} />
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
